import * as React from 'react';

import { InfoTooltip } from '../../../tooltip';
import { DATE_FORMATS, formatDate } from '../../../../utils/date.utils';
import type { TokenValueLastChange } from '../../utils/blocknote-document/types/token';

type ChangedValueIndicatorProps = {
  lastChange?: TokenValueLastChange;
};

/**
 * Audit indicator shown next to a token whose pre-filled value was changed by
 * a clinician. The tooltip surfaces who changed it, when, and the
 * original → new value.
 */
export const ChangedValueIndicator: React.FC<ChangedValueIndicatorProps> = ({
  lastChange,
}) => {
  if (!lastChange?.changeKind) {
    return null;
  }

  return (
    <InfoTooltip
      title={buildChangedTitle(lastChange)}
      description={buildChangedDescription(lastChange)}
      side="top"
    >
      <span
        aria-label="Edited value"
        className="ml-0.5 inline-flex cursor-default text-xs font-bold text-amber-500"
        contentEditable={false}
      >
        †
      </span>
    </InfoTooltip>
  );
};

const CHANGE_KIND_TITLES: Record<string, string> = {
  edited: 'Edited',
  cleared: 'Cleared',
  overridden: 'Overridden',
};

function buildChangedTitle(lastChange: TokenValueLastChange): string {
  return CHANGE_KIND_TITLES[lastChange.changeKind] ?? 'Edited';
}

function buildChangedDescription(
  lastChange: TokenValueLastChange,
): string | undefined {
  const details: string[] = [];

  const changedByDisplay = lastChange.changedByName || lastChange.changedBy;
  if (changedByDisplay) {
    details.push(`Changed by: ${changedByDisplay}`);
  }

  if (lastChange.changedAt) {
    details.push(
      `At: ${formatDate(lastChange.changedAt, {
        formatPattern: DATE_FORMATS.LONG_WITH_TIME,
      })}`,
    );
  }

  const oldValueDisplay = lastChange.oldValue || 'empty';
  const newValueDisplay = lastChange.newValue || 'empty';
  details.push(`From: ${oldValueDisplay} → ${newValueDisplay}`);

  return details.length > 0 ? details.join(' • ') : undefined;
}
